import { create } from 'zustand';

export interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  type?: 'text' | 'result' | 'error' | 'ai-rewrite' | 'download' | 'guide-button' | 'mode-select' | 'batch-confirm' | 'progress';
  data?: any;
}

export interface ResultItem {
  id: string;
  url: string;
  prompt: string;
  generateType: 'video' | 'image';
  thumbUrl: string;
  format: string;
  timestamp: number;
  downloaded?: boolean;
  filepath?: string;
}

export interface BatchTaskItem {
  id: string;
  index: number;
  prompt: string;
  reason: string;
  materials: TaskMaterial[];
  expectedEffect: string;
  duration: number;
  aspectRatio: string;
  model: string;
  status: 'pending' | 'submitted' | 'generating' | 'completed' | 'downloaded' | 'failed';
  outputFile?: string;
  error?: string;
}

export type TaskMode = 'single' | 'batch';

export interface TaskMaterial {
  path: string;
  type: 'image' | 'video';
  name?: string;
}

export interface HistoryItem {
  id: string;
  prompt: string;
  model: string;
  duration: number;
  resultUrl: string;
  thumbnailUrl?: string;
  localPath?: string;
  createdAt: number;
  status: 'completed' | 'downloaded' | 'deleted';
}

export interface UsageStats {
  totalTasks: number;
  completedTasks: number;
  failedTasks: number;
  todayTasks: number;
  credits: number;
}

export interface Settings {
  deepseekApiKey: string;
  backendUrl: string;
  model: string;
  autoDownload: boolean;
}

export type GuidedStep =
  | 'welcome'
  | 'task-drafting'
  | 'task-confirming'
  | 'batch-collecting'
  | 'task-executing'
  | 'task-done';

interface AppState {
  // 应用状态
  messages: Message[];
  isSubmitting: boolean;
  statusText: string;

  // 引导流程
  guidedStep: GuidedStep;

  // 结果
  results: ResultItem[];

  // 批量任务状态
  taskMode: TaskMode;
  batchTasks: BatchTaskItem[];

  // 作品历史
  history: HistoryItem[];

  // 用量统计
  usage: UsageStats;

  // 设置
  settings: Settings;
  activePanel: 'chat' | 'settings' | 'history';

  // 视频预览
  previewUrl: string | null;

  // Actions
  addMessage: (msg: Message) => void;
  setSubmitting: (submitting: boolean) => void;
  setStatusText: (text: string) => void;
  setGuidedStep: (step: GuidedStep) => void;
  setTaskMode: (mode: TaskMode) => void;
  setBatchTasks: (tasks: BatchTaskItem[]) => void;
  addResult: (result: ResultItem) => void;
  addHistory: (item: HistoryItem) => void;
  updateUsage: (updates: Partial<UsageStats>) => void;
  setSettings: (settings: Partial<Settings>) => void;
  setActivePanel: (panel: 'chat' | 'settings' | 'history') => void;
  setPreviewUrl: (url: string | null) => void;
}

export const useStore = create<AppState>((set) => ({
  messages: [],
  isSubmitting: false,
  statusText: '',
  guidedStep: 'welcome',
  results: [],
  taskMode: 'single',
  batchTasks: [],
  history: [],
  usage: {
    totalTasks: 0,
    completedTasks: 0,
    failedTasks: 0,
    todayTasks: 0,
    credits: 100,
  },
  settings: {
    deepseekApiKey: '',
    backendUrl: 'http://localhost:3001',
    model: 'seedance_2.0_fast',
    autoDownload: false,
  },
  activePanel: 'chat',
  previewUrl: null,

  addMessage: (msg) => set((s) => ({ messages: [...s.messages, msg] })),
  setSubmitting: (isSubmitting) => set({ isSubmitting }),
  setStatusText: (statusText) => set({ statusText }),
  setGuidedStep: (guidedStep) => set({ guidedStep }),
  setTaskMode: (taskMode) => set({ taskMode }),
  setBatchTasks: (batchTasks) => set({ batchTasks }),
  addResult: (result) => set((s) => ({ results: [result, ...s.results] })),
  addHistory: (item) => set((s) => {
    const history = [item, ...s.history].slice(0, 200);
    try { localStorage.setItem('vidclaw_history', JSON.stringify(history)); } catch {}
    return { history };
  }),
  updateUsage: (updates) => set((s) => {
    const usage = { ...s.usage, ...updates };
    return { usage };
  }),
  setSettings: (newSettings) =>
    set((s) => ({ settings: { ...s.settings, ...newSettings } })),
  setActivePanel: (activePanel) => set({ activePanel }),
  setPreviewUrl: (previewUrl) => set({ previewUrl }),
}));